import { addHours, format, formatDistance } from "date-fns";
import {
  parsePitAsUtc,
  pitToHumanReadable,
  utcFieldsAsLocalDate,
} from "@utils/time";

export interface FormatTimeTransformerDisplayParams {
  ts: string;
  tz: number;
  mode: string;
  format?: Intl.DateTimeFormatOptions;
  multiline?: boolean;
  now?: Date;
}

export interface TimeTransformerDisplay {
  formattedTime: string | string[];
  relativeDate: string;
}

export const formatTimeTransformerDisplay = ({
  ts,
  tz,
  mode,
  format: dateTimeFormat,
  multiline,
  now = new Date(),
}: FormatTimeTransformerDisplayParams): TimeTransformerDisplay | null => {
  // Naive strings are UTC (same rule as pitToHumanReadable).
  const instant = parsePitAsUtc(ts);
  if (Number.isNaN(instant.getTime())) {
    return null;
  }
  // Only the tz selector shifts the wall clock: `format` prints local fields, so give it
  // a Date whose local fields are the shifted UTC fields. Do not "cancel" the browser
  // offset with getTimezoneOffset (wrong near DST changes), and do not use a Date
  // subclass such as UTCDate (breaks under Playwright's fake `Date` from page.clock).
  const date = utcFieldsAsLocalDate(addHours(instant, tz));
  const isHumanReadable = mode === "humanReadable";

  let formattedTime: string | string[] = isHumanReadable
    ? // Pass the raw ts + tz so pitToHumanReadable applies the offset on a
      // UTC-parsed instant. Passing the pre-shifted `date` double-counted the
      // browser offset for Z-suffixed inputs.
      pitToHumanReadable(ts, false, dateTimeFormat, tz)
    : format(date, mode);

  if (multiline) {
    formattedTime = isHumanReadable
      ? formattedTime.split(", ")
      : formattedTime.split(" ");
  }

  return {
    formattedTime,
    relativeDate: formatDistance(instant, now, { addSuffix: true }),
  };
};
